import React, { useEffect, useMemo, useCallback, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classNames from 'classnames';
import { Text, ChoiceGroup } from '@fluentui/react';
import { Dropdown, Label, Stack } from '@bingads-webui-react/fabric-control';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';
import { entityIs, propertyIs, andComp } from '@bingads-webui-campaign-react/form-error';
import { PreferenceContext } from '@bingads-webui-react/preference-context';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { DefaultButton, ERROR_ENTITY, IconButton } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { MediaEditorPanel } from '@bingads-webui-campaign-react/media-editor-panel';
import { PivotItemKey } from '@bingads-webui-campaign-react/media-editor';
import { isTestEnvironment } from '@bingads-webui-campaign/global-app-init';
import { MediaRepository } from '@bingads-webui-campaign/media-repository';
import { ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import { AspectRatiosForDisplayAds, constants } from '@bingads-webui-campaign-react/image-experience-model';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { useImagePicker } from '@bingads-webui-campaign-react/add-image-hooks';
import { PreviewSingleRightRailWrapper } from '@bingads-webui-campaign-react/preview-right-rail-wrapper';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { CroppedImageView } from '@bingads-webui-campaign-react/cropped-image-view';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { useCopilotRepositoryContext } from '@bingads-webui/inline-copilot';
import { DisplayAdsEditorDrawer as DisplayAdsEditor } from '@bingads-webui-campaign-react/display-ads-editor';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { useBrandKitService } from '@bingads-webui-campaign-react/brandkit-data-service';
import { withComponentErrorLogging } from '@bingads-webui-campaign-react/global-error-options';

import { getTargetOptions, getStandardTargetOptions, getMobileTargetOptions, getAllStandardOptions, getAllMobileOptions } from './image-targets';
import { imageModelToViewModel, imagePlacementsToImage } from '../model/utl';
import { PopulateImages } from './populate-images';
import { CropPreview } from './cropped-preview';
import { placements, getPlacementFromTemplate } from '../model/constants';

const DisplayAdsEditorDrawer = withComponentErrorLogging(DisplayAdsEditor, 'DisplayAdsEditorDrawer');

const ImageContainerWidth = 170;
const ImageContainerHeight = 107;

const getStyles = () => ({
  rootContainer: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '20px',
  },
  imageContainer: {
    position: 'relative',
    height: `${ImageContainerHeight}px`,
    width: `${ImageContainerWidth}px`,
    overflow: 'hidden',
    borderRadius: '6px',
    backgroundColor: '#F3F2F1',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonContainerStyle: {
    display: 'flex',
    position: 'absolute',
    bottom: '5px',
    width: '100%',
    justifyContent: 'space-evenly',
  },
  choiceGroupStyles: {
    '& .ms-ChoiceField': {
      margin: '0',
    },
    '& label': {
      padding: '0 0 2px 0',
      fontWeight: 'normal',
    },
  },
});

const dropdownStyles = {
  dropdown: { width: 250 },
};

const editButtonStyle = {
  root: {
    borderRadius: '99px',
    height: 32,
    width: 32,
    backgroundColor: '#ffffffd4',
  },
};

export const validateImage = (/* Image, i18n */) => {
  const messages = [];

  return messages;
};

const entityIsAd = entityIs(ERROR_ENTITY.Ad);
const imageTypeFromImage = image => `Image${image.targetWidth}x${image.targetHeight}`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getImageStyles(image) {
  const contentWidth = ImageContainerWidth;
  const contentHeight = ImageContainerHeight;
  let desiredCropWidth = contentWidth;
  let desiredCropHeight = contentHeight;

  const cropImageWidth = image.cropWidth;
  const cropImageHeight = image.cropHeight;

  const cropRatio = contentHeight / cropImageHeight > contentWidth / cropImageWidth ?
    contentHeight / cropImageHeight : contentWidth / cropImageWidth;

  desiredCropHeight = cropImageHeight * cropRatio;
  desiredCropWidth = cropImageWidth * cropRatio;

  const xScale = desiredCropWidth / image.cropWidth;
  const yScale = desiredCropHeight / image.cropHeight;

  const midPointOffsetX = desiredCropWidth > contentWidth ?
    Math.ceil(-(desiredCropWidth - contentWidth) / 2) : 0;
  const midPointOffsetY = desiredCropHeight > contentHeight ?
    Math.ceil(-(desiredCropHeight - contentHeight) / 2) : 0;

  return {
    image: {
      width: image.originalWidth * xScale,
      height: image.originalHeight * yScale,
    },
    container: {
      marginLeft: `${(-image.cropX * xScale) + midPointOffsetX}px`,
      marginTop: `${(-image.cropY * yScale) + midPointOffsetY}px`,
    },
  };
}

const standardDeviceType = 'standard';
const mobileDeviceType = 'mobile';
const standardSizes = getAllStandardOptions();
const mobileSizes = getAllMobileOptions();

const getImageTypeFromSize = ((displayAd) => {
  if (!displayAd || !displayAd.image || !displayAd.image.targetHeight || !displayAd.image.targetWidth) {
    return standardDeviceType;
  }
  const { targetWidth, targetHeight } = displayAd.image;
  const findDeviceType = size => size.width === targetWidth && size.height === targetHeight;

  if (standardSizes.findIndex(findDeviceType) >=0) {
    return standardDeviceType;
  }

  if (mobileSizes.findIndex(findDeviceType) >=0) {
    return mobileDeviceType;
  }

  return standardDeviceType;
});

const CustomComponent = (props) => {
  const {
    i18n,
    displayAd,
    updateValidation,
    oDataConfig,
    campaignId,
    cid,
    aid,
    permissions,
    serverContext,
    perfMarker,
    isLoadingImage,
    creativeEditorProps,
    onRenderCustomAddImageButton,
  } = props;

  const {
    displayImagePicker,
    setDisplayImagePicker,
    selectedMediaPanelSource,
    setSelectedMediaPanelSource,
  } = useImagePicker();

  const isDisplayImageRegroupEnabled = permissions && permissions.dynamic && permissions.dynamic.IsDisplayImageRegroupEnabled;
  const scenario = useContext(ScenarioContext);
  const instrumentation = useContext(InstrumentationContext);
  const [isImageEditDisplayed, setIsImageEditDisplayed] = useState(false);
  const [isDisplayTemplateEditorOpen, setIsDisplayTemplateEditorOpen] = useState(false);
  const classes = useDefaultStyles(getStyles);
  const preferenceService = useContext(PreferenceContext);

  const [deviceFormatType, setDeviceFormatType] = useState(standardDeviceType);
  const mediaRepository = useMemo(() => new MediaRepository({
    cid,
    aid,
    cmpId: campaignId,
    oDataConfig,
  }), [aid, campaignId, cid, oDataConfig]);

  const currentPlacementIndex = useMemo(() => {
    if (displayAd && displayAd.image && displayAd.image.targetWidth && displayAd.image.targetHeight) {
      const type = imageTypeFromImage(displayAd.image);
      const imagePlacementIndex = _.findIndex(displayAd.imagePlacements, placement => placement.type === type);
      return imagePlacementIndex;
    }
    return -1;
  }, [displayAd]);

  const copilotRepository = useCopilotRepositoryContext();
  const { brandKitDataService, brandkitApiState } = useBrandKitService();

  useEffect(() => {
    const shouldDirty = !(_.isUndefined(displayAd) || _.isNull(displayAd));
    if (isDisplayImageRegroupEnabled === true) {
      const deviceType = getImageTypeFromSize(displayAd);
      setDeviceFormatType(deviceType);
    }

    // update on mount.
    updateValidation(displayAd && (displayAd.image || isLoadingImage) ? validateImage(displayAd.image, i18n) : [i18n.getString(_TL_('Please add an image.'))], {
      image: displayAd ? displayAd.image : null,
      imagePlacements: displayAd ? displayAd.imagePlacements : null,
      creativeData: displayAd ? displayAd.creativeData : null,
    }, shouldDirty);
  }, [displayAd, i18n, isLoadingImage, updateValidation, isDisplayImageRegroupEnabled]);

  const onEdit = useCallback(() => {
    if (displayAd && displayAd.creativeData) {
      if (
        displayAd.creativeData.creativeMeta &&
        displayAd.creativeData.creativeMeta.template &&
        Number(displayAd.creativeData.creativeMeta.template.version) >= 1.2
      ) {
        setIsDisplayTemplateEditorOpen(true);
      } else {
        setDisplayImagePicker(true);
      }
    } else {
      setIsImageEditDisplayed(true);
    }
  }, [displayAd, setDisplayImagePicker]);

  const onRemoveImage = useCallback(() => {
    updateValidation([i18n.getString(_TL_('Please add an image.'))], {
      image: null,
      creativeData: null,
    });
  }, [i18n, updateValidation]);

  const dropdownOptions = useMemo(() => {
    if (isDisplayImageRegroupEnabled === true) {
      if (deviceFormatType === 'mobile') {
        return getMobileTargetOptions({
          i18n,
          sourceWidth: displayAd && displayAd.image ? displayAd.image.originalWidth : null,
          sourceHeight: displayAd && displayAd.image ? displayAd.image.originalHeight : null,
        });
      }

      return getStandardTargetOptions({
        i18n,
        sourceWidth: displayAd && displayAd.image ? displayAd.image.originalWidth : null,
        sourceHeight: displayAd && displayAd.image ? displayAd.image.originalHeight : null,
      });
    }

    return getTargetOptions({
      i18n,
      sourceWidth: displayAd && displayAd.image ? displayAd.image.originalWidth : null,
      sourceHeight: displayAd && displayAd.image ? displayAd.image.originalHeight : null,
    });
  }, [i18n, displayAd, deviceFormatType, isDisplayImageRegroupEnabled]);

  const deviceFormatOptions = useMemo(() => [
    {
      key: standardDeviceType,
      text: i18n.getString(_TL_('Standard')),
    }, {
      key: mobileDeviceType,
      text: i18n.getString(_TL_('Mobile')),
    },
  ], [i18n]);

  const onChangeTarget = useCallback((event, item, selectItem) => {
    const image = displayAd && displayAd.image ? displayAd.image : {};
    const currentItem = item || selectItem;
    if (image) {
      const imagePlacementIndex = _.findIndex(displayAd.imagePlacements, placement => placement.type === currentItem.key);
      if (imagePlacementIndex >=0) {
        const imageViewModel = imageModelToViewModel(displayAd.imagePlacements[imagePlacementIndex]);

        updateValidation([], {
          image: {
            ...imageViewModel,
          },
          imagePlacements: displayAd.imagePlacements,
        });
      }
    } else {
      updateValidation([i18n.getString(_TL_('Please add an image.'))], {
        image: {
          targetWidth: currentItem.width,
          targetHeight: currentItem.height,
        },
      });
    }
  }, [displayAd, i18n, updateValidation]);

  const selectedItem = useMemo(() => {
    if (displayAd && displayAd.image && displayAd.image.targetWidth && displayAd.image.targetHeight) {
      if (isDisplayImageRegroupEnabled === true) {
        const imageKey = imageTypeFromImage(displayAd.image);
        const target = dropdownOptions.find(option => option.key === imageKey);
        if (target) {
          return imageKey;
        }

        const firstItem = dropdownOptions.find(option => !option.itemType);
        if (firstItem) {
          firstItem.key;
          onChangeTarget(null, null, firstItem);
        }
      }

      return imageTypeFromImage(displayAd.image);
    }
    return undefined;
  }, [displayAd, dropdownOptions, onChangeTarget, isDisplayImageRegroupEnabled]);

  const minCropSize = useMemo(() => {
    if (displayAd && displayAd.imagePlacements && displayAd.imagePlacements[currentPlacementIndex]) {
      return constants.getMinimumSizeForImage(
        displayAd.imagePlacements[currentPlacementIndex].type,
        false,
        constants.defaultImageOption,
        false,
        false,
        true
      );
    }
    return null;
  }, [currentPlacementIndex, displayAd]);

  const baseImage = useMemo(() => {
    if (displayAd && displayAd.imagePlacements && displayAd.imagePlacements[currentPlacementIndex]) {
      const base = displayAd.imagePlacements[currentPlacementIndex];
      return {
        Type: base.type,
        Src: base.src,
        Width: base.width,
        Height: base.height,
      };
    }
    return null;
  }, [currentPlacementIndex, displayAd]);

  const onSaveEditImage = useCallback(({ cropSettings }) => {
    const base = displayAd.imagePlacements[currentPlacementIndex];

    base.cropSettings = cropSettings;

    updateValidation([], {
      image: {
        ...displayAd.image,
        cropWidth: cropSettings.SourceWidth,
        cropHeight: cropSettings.SourceHeight,
        cropX: cropSettings.SourceX,
        cropY: cropSettings.SourceY,
      },
      imagePlacements: displayAd.imagePlacements,
      creativeData: displayAd.creativeData,
    });

    setIsImageEditDisplayed(false);
  }, [currentPlacementIndex, displayAd, updateValidation]);

  const onNewImageSelected = useCallback((imagePlacements) => {
    if (imagePlacements && imagePlacements[0] && imagePlacements[0].creativeData) {
      updateValidation([], {
        ...imagePlacements[0],
      });
    } else {
      const newAdData = imagePlacementsToImage({ crops: imagePlacements[0] });
      updateValidation([], {
        ...newAdData.data,
      });
    }
    setDisplayImagePicker(false);
  }, [setDisplayImagePicker, updateValidation]);

  const placementTitle = useMemo(() => {
    if (!creativeEditorProps) {
      return '';
    }
    const { creativeData } = creativeEditorProps || {};

    const { creativeMeta } = creativeData || {};

    const { placement, template = {} } = creativeMeta || {};

    if (!placement) {
      const placementFromTemplate = getPlacementFromTemplate(template);

      return placements[placementFromTemplate].name;
    }
    return placements[placement].name;
  }, [creativeEditorProps]);

  const resizeImageHeightForThumbnail = useMemo(() => {
    if (displayAd && displayAd.image && displayAd.image.cropHeight && displayAd.image.cropWidth) {
      const aspectRatio = displayAd.image.cropWidth / displayAd.image.cropHeight;
      const maxWidth = 170;
      return Math.min(maxWidth / aspectRatio, 107);
    }
    return 0;
  }, [displayAd]);

  const imageControl = useMemo(() => {
    if (!displayAd) {
      return null;
    }

    return (
      <div className={classes.rootContainer}>
        <div className={classes.imageContainer}>
          <div>
            {!isLoadingImage && displayAd.image &&
            (
            <CroppedImageView
              src={displayAd.image.src}
              originalWidth={displayAd.image.originalWidth}
              aspectRatio={displayAd.image.cropWidth / displayAd.image.cropHeight}
              resizeImageHeight={resizeImageHeightForThumbnail}
              SourceWidth={displayAd.image.cropWidth}
              SourceX={displayAd.image.cropX}
              SourceY={displayAd.image.cropY}
            />
            )
          }
          </div>
          <div
            className={classNames(['edit-button', classes.buttonContainerStyle])}
          >
            <IconButton
              ariaLabel={i18n.getString(_TL_('Edit Image'))}
              onClick={onEdit}
              iconProps={{ iconName: 'Edit' }}
              className="edit-icon-button"
              styles={editButtonStyle}
            />
            <IconButton
              ariaLabel={i18n.getString(_TL_('Remove Image'))}
              onClick={onRemoveImage}
              iconProps={{ iconName: 'Delete' }}
              className="delete-icon-button"
              styles={editButtonStyle}
            />
          </div>
        </div>
        {
          creativeEditorProps ?
            <Text>
              {i18n.getString(placementTitle)}
            </Text>
          :
            <div>
              {isDisplayImageRegroupEnabled ===true &&
                (
                  <div>
                    <div>
                      <Label>
                        <Stack horizontal verticalAlign="center" >
                          {i18n.getString(_TL_('Device format'))}
                          <HelpPopupInline topic="pop_BA_CampaignCreate_AudTargets_Device" />
                        </Stack>
                      </Label>
                    </div>
                    <ChoiceGroup
                      options={deviceFormatOptions}
                      onChange={(e, item) => setDeviceFormatType(item.key)}
                      selectedKey={deviceFormatType}
                      className={classes.choiceGroupStyles}
                    />
                    <div>
                      <Label>
                        <Stack horizontal verticalAlign="center" >
                          {i18n.getString(_TL_('Size'))}
                          <HelpPopupInline topic="pop_BA_DisplayAds_Size" />
                        </Stack>
                      </Label>
                    </div>
                  </div>
                )
              }
              <Dropdown
                selectedKey={selectedItem}
                onChange={onChangeTarget}
                placeholder={i18n.getString(_TL_('Placement size'))}
                options={dropdownOptions}
                styles={dropdownStyles}
              />
            </div>
        }
      </div>
    );
  }, [classes.choiceGroupStyles, classes.buttonContainerStyle, classes.imageContainer, classes.rootContainer, isDisplayImageRegroupEnabled, deviceFormatType, deviceFormatOptions, creativeEditorProps, displayAd, dropdownOptions, i18n, isLoadingImage, onChangeTarget, onEdit, onRemoveImage, placementTitle, resizeImageHeightForThumbnail, selectedItem]);

  const AddImageButton = useMemo(() => {
    if (onRenderCustomAddImageButton) {
      return onRenderCustomAddImageButton({
        setDisplayImagePicker,
      });
    }

    return (
      <DefaultButton
        className="add-image-button"
        iconProps={{ iconName: 'Add' }}
        text={i18n.getString(_TL_('Add image'))}
        onClick={() => setDisplayImagePicker(true)}
      />
    );
  }, [i18n, onRenderCustomAddImageButton, setDisplayImagePicker]);

  return (
    <div>
      <div>
        {displayAd && (displayAd.image || isLoadingImage) ?
        imageControl : AddImageButton
        }
      </div>
      <PopulateImages
        showAddButton={false}
        aid={aid}
        campaignId={campaignId}
        i18n={i18n}
        perfMarker={perfMarker}
        permissions={permissions}
        oDataConfig={oDataConfig}
        serverContext={serverContext}
        onNewImageSelected={onNewImageSelected}
        selectedMediaPanelSource={selectedMediaPanelSource}
        setSelectedMediaPanelSource={setSelectedMediaPanelSource}
        displayImagePicker={displayImagePicker}
        setDisplayImagePicker={setDisplayImagePicker}
        maxUploadNum={1}
        fullUrl={displayAd && displayAd.FinalUrls && displayAd.FinalUrls.length > 0 ? displayAd.FinalUrls[0] : null}
        creativeEditorProps={creativeEditorProps}
      />
      <MediaEditorPanel
        isOpen={isImageEditDisplayed}
        onDismiss={() => setIsImageEditDisplayed(false)}
        onApply={onSaveEditImage}
        mediaRepository={mediaRepository}
        aspectRatioConfig={AspectRatiosForDisplayAds}
        cropSettings={displayAd && displayAd.imagePlacements && displayAd.imagePlacements[currentPlacementIndex] ? displayAd.imagePlacements[currentPlacementIndex].cropSettings : null}
        minCropSize={minCropSize}
        baseImage={baseImage}
        hasRightRail
        onRenderRightRailContent={({
          previewImageUrl,
          cropSettings = {},
        }) => {
          const {
           SourceWidth, SourceX, SourceY,
            } = cropSettings || {};
          const aspectRatio = displayAd.image.cropWidth / displayAd.image.cropHeight;
          const maxWidth = 500;
          const resizeImageHeight = Math.min(maxWidth / aspectRatio, 600);

            return (
              <PreviewSingleRightRailWrapper
                previewTitle={i18n.getString('Ad preview')}
                previewSettings={{
                  src: previewImageUrl,
                  originalWidth: displayAd.image.originalWidth,
                  aspectRatio,
                  resizeImageHeight,
                  SourceX,
                  SourceY,
                  SourceWidth,
                }}
                Preview={CropPreview}
              />
            );
        }}
        adCreatorScenario={{
          scenarioName: `${scenario.name}AdCreation`,
          scenarioValue: `${scenario.name}AdCreation`,
        }}
        isThumbsUpDownEnabled={serverContext.IsAADUser}
        isTestEnv={isTestEnvironment(document.domain)}
        disabledPivotItemKeys={[PivotItemKey.Effect]}
        customizedDefaultSelectedPivotItemKey={PivotItemKey.Crop}
      />
      {
        isDisplayTemplateEditorOpen &&
        <DisplayAdsEditorDrawer
          containerClassName={classes.editorContainer}
          i18n={i18n}
          hasFetchBrandKitData={props.useBrandKit}
          perfMarker={perfMarker}
          brandKitDataService={brandKitDataService}
          brandkitApiState={brandkitApiState}
          permissions={permissions}
          oDataConfig={oDataConfig}
          serverContext={serverContext}
          instrumentation={instrumentation}
          url={displayAd && displayAd.FinalUrls && displayAd.FinalUrls.length > 0 ? displayAd.FinalUrls[0] : null}
          showNavigation
          showSummaryBarControl
          preferenceService={preferenceService}
          displayTemplatesDataService={copilotRepository}
          isOpen={isDisplayTemplateEditorOpen}
          setIsOpen={setIsDisplayTemplateEditorOpen}
          imageData={{
            ...displayAd.image,
            metadata: displayAd.creativeData.creativeMeta,
          }}
          onApplyCreative={creativeEditorProps.onApplyCreative}
          scenario={scenario}
          aid={aid}
          cid={cid}
          sourceType={displayAd.image.originalSourceType}
        />
      }
    </div>
  );
};

CustomComponent.propTypes = {
  // onDataChange: PropTypes.func.isRequired,
  // idx: PropTypes.number.isRequired,
  displayAd: PropTypes.shape({
    FinalUrls: PropTypes.arrayOf(PropTypes.string),
    image: PropTypes.shape({
      src: PropTypes.string,
      originalWidth: PropTypes.number,
      originalHeight: PropTypes.number,
      cropWidth: PropTypes.number,
      cropHeight: PropTypes.number,
      cropX: PropTypes.number,
      cropY: PropTypes.number,
      targetHeight: PropTypes.number,
      targetWidth: PropTypes.number,
    }),
    imagePlacements: PropTypes.arrayOf(PropTypes.shape({
      src: PropTypes.string,
      width: PropTypes.number,
      height: PropTypes.number,
      type: PropTypes.string,
      cropSettings: PropTypes.shape({
        SourceX: PropTypes.number,
        SourceY: PropTypes.number,
        SourceWidth: PropTypes.number,
        SourceHeight: PropTypes.number,
      }),
    })),
    creativeData: PropTypes.shape({
      creativeMeta: PropTypes.shape({
        placement: PropTypes.string,
        template: PropTypes.shape({ version: PropTypes.string }),
      }),
    }),
    messages: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  campaignId: PropTypes.number.isRequired,
  cid: PropTypes.number.isRequired,
  aid: PropTypes.number.isRequired,
  perfMarker: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      IsDisplayImageRegroupEnabled: PropTypes.bool,
      BrandKitUI: PropTypes.bool,
    }).isRequired,
  }).isRequired,
  serverContext: PropTypes.shape({
    IsAADUser: PropTypes.bool,
  }).isRequired,
  isLoadingImage: PropTypes.bool.isRequired,
  creativeEditorProps: PropTypes.shape({
    onApplyCreative: PropTypes.func,
  }),
  onRenderCustomAddImageButton: PropTypes.func,
  useBrandKit: PropTypes.bool,
};

CustomComponent.defaultProps = {
  creativeEditorProps: undefined,
  onRenderCustomAddImageButton: undefined,
};

const ImageExperienceWithValidation = withServerContext(withControlErrorHandling(withUnvalidatedControlErrors(withError(withCustomValidation(CustomComponent)))));

export const ValidatedImageExperience = (props) => {
  const {
    displayAd, idx, i18n, ...rest
  } = props;

  const ImageProp = andComp(entityIsAd, propertyIs(`Image.ImageMedia_${idx}`));

  const dataObj = {
    image: (displayAd && displayAd.image) || null,
    imagePlacements: (displayAd && displayAd.imagePlacements) || null,
  };

  return (
    <ImageExperienceWithValidation
      {...rest}
      displayAd={displayAd}
      idx={idx}
      i18n={i18n}
      validationProps={{
        field: 'Image',
        data: dataObj,
        initialValue: dataObj,
      }}
      formErrorProps={{ filter: ImageProp }}
    />
  );
};

ValidatedImageExperience.propTypes = {
  displayAd: PropTypes.shape({
    image: PropTypes.shape({ }),
    imagePlacements: PropTypes.arrayOf(PropTypes.shape({})),
    messages: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  idx: PropTypes.number.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
};
